import React, { useState, useEffect, useContext } from "react";
import { Button, Card, Form, Container, Row, Col, Table } from "react-bootstrap";
import SweetAlert from "react-bootstrap-sweetalert";
import { useHistory } from "react-router-dom";
import { apiService } from "../../../service/ApiService";
import { notificationService } from "../../../service/NotificationService";
import CreateButton from "components/Buttons/CreateButton";
import EditButton from "components/Buttons/EditButton";
import UserCreditsContext from "contexts/UserCreditsContext";
import { useParams } from "react-router";
import ExpirationColumn from "components/Custom/ExpirationColumn";
import LicenseStatusColumn from "components/Custom/LicenseStatusColumn";

import { useTranslation } from "react-i18next";

function LicensePage() {
  const {
    t,
  } = useTranslation();

  const backRoute = "/admin/licenses";
  const history = useHistory();
  const { id } = useParams();

  const [attributes, setAttributes] = useState([]);
  const [activeAttributes, setActiveAttributes] = useState([]);
  const [preselectedAttributes, setPreselectedAttributes] = useState([]);
  const [totalCost, setTotalCost] = useState(0);
  const [existedLicenseCost, setExistedLicenseCost] = useState(0);
  const [initialQuantities, setInitialQuantities] = useState([]);
  const { refreshCredits, setRefreshCredits } = useContext(UserCreditsContext);

  const [friendlyName, setFriendlyName] = useState("");
  const [assignedUser, setAssignedUser] = useState("");
  const [storeId, setStoreId] = useState(null);
  const [version, setVersion] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);

  const [errorMessage, setErrorMessage] = useState("");
  const [isDisabled, setIsDisabled] = useState(false);
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    if (id) {
      apiService
        .get(`api/v1/licenses/${id}`)
        .then((license) => {
          fetchAttributes(license.licenseAttributes);
          setExistedLicenseCost(license.totalCost);
          setPreselectedAttributes(license.licenseAttributes);
          setInitialQuantities(license.licenseAttributes.map(({ id, quantity }) => ({ id, quantity })));
          fillFields(license);
        })
        .catch((error) => {
          notificationService.notifyError(error.message);
          history.push(backRoute);
        });
    }
  }, []);

  const handleScroll = (e) => {
    e.target.blur();
  };

  const save = () => {
    setIsDisabled(true);
    const license = {
      friendlyName: friendlyName,
      attributes: activeAttributes,
      version: version != null ? version + 1 : undefined,
      storeId: storeId ? storeId : null,
    };

    if (id) {
      license.assignedUserId = assignedUser;
      apiService
        .putJson(`api/v1/licenses/${id}`, license)
        .then((resp) => {
          notificationService.notifySuccess(t("messages.licenseUpdatedSuccessfully"));
          history.push(backRoute);
          setRefreshCredits(true);
          setIsDisabled(false);
        })
        .catch((error) => {
          setErrorMessage(error.message);
          notificationService.notifyError(error.message);
          setIsDisabled(false);
        });
    } else {
      apiService
        .postJson("api/v1/licenses", license)
        .then((resp) => {
          notificationService.notifySuccess(t("messages.licenseCreatedSuccessfully"));
          history.push(backRoute);
          setRefreshCredits(true);
          setIsDisabled(false);
        })
        .catch((error) => {
          setErrorMessage(error.message);
          notificationService.notifyError(error.message);
          setIsDisabled(false);
        });
    }
  };

  const fillFields = (license) => {
    setFriendlyName(license.friendlyName);
    setAssignedUser(license.assignedUserId);
    setVersion(license.version);
    setExpiresAt(license.expiresAt);
  };

  const calculateLicenseCost = () => {
    let totalCost = 0;
    activeAttributes.forEach((el) => (totalCost += Number(el.quantity) * el.attribute.credits));
    setTotalCost(totalCost);
  };

  const calculateLicenseNewCost = () => { 
    let totalCost = existedLicenseCost;
    activeAttributes.forEach((el) => {
        const foundObj = initialQuantities.find((item) => item.id === el.id);
        if (foundObj) {
          const initialQuantity = foundObj.quantity;
          if (initialQuantity > 0 && el.quantity > initialQuantity) {
            totalCost += (el.quantity - initialQuantity) * el.attribute.credits;
          } else if (initialQuantity > 0 && el.quantity === initialQuantity) {
            return;
          } else totalCost += Number(el.quantity) * el.attribute.credits;
        } else {
          totalCost += Number(el.quantity) * el.attribute.credits;
        }
    });
    setTotalCost(totalCost);
  };

  const calculateRenewalCost = (getQuantity) =>
    attributes.reduce((sum, item) => sum + Number(getQuantity(item)) * Number(item.attribute.renewalCost || 0), 0);

  const savedQuantity = (item) => preselectedAttributes.find((el) => el.id === item.id)?.quantity || 0;

  const renewalCost = calculateRenewalCost((item) => activeAttributes.find((el) => el.id === item.id)?.quantity ?? savedQuantity(item));

  const alertRenew = () => {
    setAlert(
      <SweetAlert
        warning
        style={{ display: "block", marginTop: "-100px" }}
        title={t("messages.renewLicense")}
        onConfirm={renew}
        onCancel={() => setAlert(null)}
        confirmBtnBsStyle="success"
        cancelBtnBsStyle="default"
        confirmBtnText={t("common.actions.renew")}
        cancelBtnText={t("common.actions.cancel")}
        showCancel
      >
        {t("messages.doYouWantToRenewLicense", { name: friendlyName, cost: calculateRenewalCost(savedQuantity) })}
      </SweetAlert>
    );
  };

  const renew = async () => {
    setAlert(null);
    setIsDisabled(true);
    try {
      await apiService.post(`api/v1/licenses/${id}/renew`);
      notificationService.notifySuccess(t("messages.licenseRenewedSuccessfully"));
      setRefreshCredits(true);
      history.push(backRoute);
    } catch (error) {
      setErrorMessage(error.message);
      notificationService.notifyError(error.message);
    } finally {
      setIsDisabled(false);
    }
  };

  useEffect(() => {
    calculateLicenseCost();
  }, [activeAttributes]);

  useEffect(() => {
    if (!id) return;
    calculateLicenseNewCost();
  }, [activeAttributes, existedLicenseCost]);

  const fetchAttributes = (preselectedAttributes) => {
    apiService
      .get(`api/v1/attributes`)
      .then((response) => {
        const attributesToAssign = [];
        response.data.forEach((attribute) => {
          const attributeToAssign = {
            id: attribute.id,
            attribute: attribute,
            quantity: 0,
            selected: false,
          };
          attributesToAssign.push(attributeToAssign);
        });
        if (!id) {
          setAttributes(attributesToAssign);
        } else {
          const formattedPreselectedAttributes = [];
          preselectedAttributes.forEach((preselectedAttribute) => {
            const attributeToAssign = {
              id: preselectedAttribute.id,
              attribute: attributesToAssign.find((attribute) => attribute.id === preselectedAttribute.id).attribute,
              quantity: preselectedAttribute.quantity,
              selected: true,
            };
            formattedPreselectedAttributes.push(attributeToAssign);
          });
          const combined = [...attributesToAssign, ...formattedPreselectedAttributes];
          setAttributes(Array.from(new Map(combined.map((item) => [item["id"], item])).values()));
        }
      })
      .catch((error) => {
        notificationService.notifyError(error.message);
        history.push(backRoute);
      });
  };

  useEffect(() => {
    if (id) return;
    fetchAttributes();
  }, []);

  const onAssignQuantity = (id, event) => {
    const selectedAttribute = attributes.find((el) => el.id === id);
    selectedAttribute.quantity = event.target.value && event.target.value > 0 ? Number(event.target.value) : 0;
    const attrIndex = activeAttributes.findIndex((el) => el.id === selectedAttribute.id);

    if (attrIndex === -1) {
      activeAttributes.push(selectedAttribute);
    } else {
      if (selectedAttribute.quantity === 0) activeAttributes.splice(attrIndex, 1);
      else activeAttributes[attrIndex] = selectedAttribute;
    }
    setActiveAttributes([...activeAttributes]);
  };

  const onIncreaseQuantity = (id, event) => {
    const selectedAttribute = attributes.find((el) => el.id === id);
    const inputValue = Number(event.target.value);
    const savedAttr = preselectedAttributes.find((el) => el.id === id);
    const savedQuantity = savedAttr?.quantity || 0;
    const newQuantity = isNaN(inputValue) || inputValue <= 0 ? 0 : inputValue;
  
    if (savedAttr && newQuantity < savedQuantity) {
      return;
    }

    selectedAttribute.quantity = newQuantity;

    const attrIndex = activeAttributes.findIndex((el) => el.id === selectedAttribute.id);

    if (attrIndex === -1) {
      activeAttributes.push(selectedAttribute);
    } else {
      if (selectedAttribute.quantity === 0 || selectedAttribute.quantity === savedQuantity) {
        activeAttributes.splice(attrIndex, 1);
      } else if (selectedAttribute.quantity === savedQuantity) {
        selectedAttribute.quantity = savedQuantity;
      }
      else {
        activeAttributes[attrIndex] = selectedAttribute;
      }
    }

    setActiveAttributes([...activeAttributes]);
  };

  const onAssignSelection = (id, event) => {
    const selectedAttribute = attributes.find((el) => el.id === id);
    const attrIndex = activeAttributes.findIndex((el) => el.id === selectedAttribute.id);
    selectedAttribute.credits = selectedAttribute.attribute.credits;
    selectedAttribute.quantity = 1;

    if (event.target.checked) {
      activeAttributes.push(selectedAttribute);
    } else {
      activeAttributes.splice(attrIndex, 1);
    }

    setActiveAttributes([...activeAttributes]);
  };

  return (
    <>
      {alert}
      <Container fluid>
        <Row>
          <Col md="12">
            <Card className="stacked-form">
              <Card.Header>
                <Card.Title as="h4">{id ? t("pages.titles.editLicense") : t("pages.titles.createLicense")}</Card.Title>
              </Card.Header>
              <Card.Body>
                {errorMessage ? <div style={{ color: "crimson" }}>{errorMessage}</div> : null}
                <Form action="#" method="#">
                  <Row>
                    <Col md={id ? 6 : 12}>
                      <Form.Group>
                        <label>{t("common.labels.friendlyName")}</label>
                        <Form.Control
                          required={true}
                          placeholder={t("common.labels.friendlyName")}
                          type="text"
                          value={friendlyName}
                          onChange={(event) => setFriendlyName(event.target.value)}
                        ></Form.Control>
                      </Form.Group>
                    </Col>
                    {id ? (
                      <Col md={6}>
                        <Form.Group>
                          <label>
                            {t("tables.headers.expiresAt")} / {t("tables.headers.status")}
                          </label>
                          <Form.Control as="div" readOnly>
                            <ExpirationColumn timestamp={expiresAt} /> - <LicenseStatusColumn expiresAt={expiresAt} />
                          </Form.Control>
                        </Form.Group>
                      </Col>
                    ) : null}
                  </Row>
                  <Form.Group>
                    <label>{t("common.labels.featuresToActivate")}</label>
                    {attributes && attributes.length > 0 ? (
                      <div>
                        <Form.Text className="text-muted" style={{ fontSize: 18 }}>
                          {" "}
                          {t("messages.licenseCostSuffix")}{totalCost}
                        </Form.Text>
                        <Form.Text className="text-muted" style={{ fontSize: 18 }}>
                          {" "}
                          {t("messages.licenseRenewalCostSuffix")}{renewalCost}
                        </Form.Text>
                        <Table className="table-hover table-striped w-full">
                          <thead>
                            <tr>
                              <th>{t("tables.headers.designation")}</th>
                              <th>{t("tables.headers.cost")}</th>
                              <th>{t("tables.headers.renewalCost")}</th>
                              <th>{t("common.actions.selectUppercase")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {attributes.map((item) => (
                              <tr key={item.id}>
                                <td>{item.attribute.name}</td>
                                <td>{item.attribute.credits}</td>
                                <td>{item.attribute.renewalCost}</td>
                                <td>
                                  {id ? (
                                    item.attribute.reusable ? (
                                      <Form.Control
                                        placeholder={t("common.labels.quantity")}
                                        type="number"
                                        onChange={(event) => onIncreaseQuantity(item.id, event)}
                                        onWheel={handleScroll}
                                        value={item.quantity}
                                      ></Form.Control>
                                    ) : (
                                      <Form.Check>
                                        <Form.Check.Label>
                                          <Form.Check.Input
                                            disabled={item.attribute.reusable || preselectedAttributes.some((el) => el.id === item.id)}
                                            type="checkbox"
                                            onChange={(event) => onAssignSelection(item.id, event)}
                                            checked={
                                              activeAttributes.some((attr) => attr.id === item.id) ||
                                              preselectedAttributes.some((el) => el.id === item.id)
                                            }
                                          ></Form.Check.Input>
                                          <span className="form-check-sign"></span>
                                        </Form.Check.Label>
                                      </Form.Check>
                                    )
                                  ) : item.attribute.reusable ? (
                                    <Form.Control
                                      placeholder={t("common.labels.quantity")}
                                      type="number"
                                      onChange={(event) => onAssignQuantity(item.id, event)}
                                      onWheel={handleScroll}
                                      min="0"
                                    ></Form.Control>
                                  ) : (
                                    <Form.Check>
                                      <Form.Check.Label>
                                        <Form.Check.Input
                                          disabled={item.attribute.reusable}
                                          type="checkbox"
                                          onChange={(event) => onAssignSelection(item.id, event)}
                                        ></Form.Check.Input>
                                        <span className="form-check-sign"></span>
                                      </Form.Check.Label>
                                    </Form.Check>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    ) : (
                      <div>{t("messages.noFeaturesAreAvailable")}</div>
                    )}
                  </Form.Group>
                </Form>
                <Row className="float-right">
                  {id ? (
                    <Col xs="auto">
                      <Button disabled={isDisabled} className="btn-wd btn-outline mr-1" variant="success" onClick={alertRenew}>
                        <i className="fa fa-sync" />
                        <span>{t("common.actions.renew")}</span>
                      </Button>
                    </Col>
                  ) : null}
                  <Col>{id ? <EditButton handler={save} disabled={isDisabled} /> : <CreateButton handler={save} disabled={isDisabled} />}</Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default LicensePage;
